import { useEffect, useState } from 'react';
import { getDashboardUrl } from '@client/api/MetabaseApi';
import auth from '@client/components/auth';
import { useGetInfluencersQuery } from '../../../generated/graphql';
import * as enums from '@playbook-technologies/common-enums';
import Select from 'react-select';
import { compact } from 'lodash';
import { TableLoader } from '../../new/components/TableShared/TableLoader';
import { PATHS } from '@client/routes';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';

export function SubscriptionAnalyticsDashboard() {
  const isSingleCreatorView = !window.location.pathname.endsWith(
    PATHS.ANALYTICS,
  );
  const influencerId = useInfluencerId();

  const adminViewInCreatorContext =
    auth.isAdministrator() && isSingleCreatorView && influencerId;
  const currentUser = auth.getUser();
  const currentUserValue = {
    value: adminViewInCreatorContext ? influencerId : currentUser.influencerId,
    label: adminViewInCreatorContext
      ? ''
      : `${currentUser.influencer?.firstName} ${currentUser.influencer?.lastName} (${currentUser.influencerId})`,
  };
  const playbookOrganicValue = {
    value: null,
    label: 'Playbook (Organic)',
  };
  const [selectedInfluencer, setSelectednfluencer] = useState(
    currentUser.influencerId ? currentUserValue : playbookOrganicValue,
  );
  const [isLoading, setIsLoading] = useState(false);
  const influencersQuery = useGetInfluencersQuery({
    variables: {
      mode: enums.Influencer.Mode.Live.value,
    },
  });

  const [fullScreen, setFullscreen] = useState(false);
  const [consumerFunnelUrl, setConsumerFunnelUrl] = useState('');
  useKeyPress('Escape', function onEscPress() {
    setFullscreen((x) => !x);
  });

  const onInit = async () => {
    setIsLoading(true);
    try {
      const { url } = await getDashboardUrl('SubscriptionAnalytics', {
        influencerId: influencerId || selectedInfluencer.value || '',
      });
      setConsumerFunnelUrl(url);
    } catch (e) {
      console.error('Failed to load analytics dashboard', e);
      setConsumerFunnelUrl('');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    onInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInfluencer, influencerId]);

  const liveInfluencersList = influencersQuery.data?.creators || [];
  const influencerSuggestions = liveInfluencersList.map((i) => {
    const fullName = i.firstName + ' ' + i.lastName;
    return {
      value: i.id,
      label: fullName + ` (${i.id})`,
    };
  });

  const display = {
    influencerSelect:
      !isSingleCreatorView &&
      (auth.isAdministrator() || auth.isModerator() || auth.isContentEditor()),
  };

  return (
    <div>
      <div>
        {display.influencerSelect && (
          <div>
            <InfluencerSelect
              options={influencerSuggestions.concat(
                compact([
                  playbookOrganicValue,
                  currentUser.influencerId ? currentUserValue : undefined,
                ]),
              )}
              value={selectedInfluencer}
              onChange={setSelectednfluencer}
            />
          </div>
        )}
      </div>
      {isLoading ? (
        display.influencerSelect ? (
          <TableLoader rows={5} height={30} color="#fafafa" />
        ) : null
      ) : (
        <div>
          <iframe
            src={consumerFunnelUrl}
            width="100%"
            style={
              fullScreen
                ? {
                    width: 'calc(100vw - 100px)',
                    height: '100vh',
                    position: 'fixed',
                    left: 100,
                    top: 0,
                  }
                : { height: 'calc(100vh - 200px)' }
            }
          />
        </div>
      )}
    </div>
  );
}

const useKeyPress = (targetKey, onPress = () => null) => {
  const [keyPressed, setKeyPressed] = useState(false);

  const downHandler = ({ key }) => {
    if (key === targetKey) {
      setKeyPressed(true);
      onPress();
    }
  };

  const upHandler = ({ key }) => {
    if (key === targetKey) setKeyPressed(false);
  };

  useEffect(() => {
    window.addEventListener('keydown', downHandler);
    window.addEventListener('keyup', upHandler);

    return () => {
      window.removeEventListener('keydown', downHandler);
      window.removeEventListener('keyup', upHandler);
    };
  }, []);

  return keyPressed;
};

type InfluencerSelectProps = {
  value: unknown;
  options: Array<{
    value: unknown;
    label: string;
  }>;
  onChange: (v: unknown) => void;
};

function InfluencerSelect({ options, onChange, value }: InfluencerSelectProps) {
  return (
    <Select
      options={options}
      onChange={onChange}
      value={value}
      isMulti={false}
    />
  );
}
